// @ts-ignore no types
import React, { useMemo, useRef, useEffect } from 'react'
// @ts-ignore no types
import { flushSync } from 'react-dom'
import _ from 'lodash'
import { enums } from '@adc/sdk'
import type { Listing } from '@adc/sdk/types'
import * as blueprint from '@adc/blueprint'
import { Address } from 'common/components/address'
import type {
  ListingPageActionsType,
  ListingPageType,
} from '@adc/blueprint/lib/listing/pdp/hooks'
// @ts-ignore no types
import Helmet from 'react-helmet'
import * as graph from '@adc/sdk'
import { connect } from 'react-redux'
// @ts-ignore no types
import { standardSchemas } from '@adc/analytics'
import config from '@adc/configment'
import {
  AdcSearch,
  AuctionApplication,
  ChatButton,
  NullComponent,
  useApplicationContext,
  withApplicationContext,
  ApplicationNotificationsConsumer,
  TutorialTooltipActions,
} from '@adc/ui-adc'
import type { FeatureFlags } from '@adc/ui-adc'
import { MarketingAnalysisProvider } from 'common/hooks/useSelectMarketAnalysisCard'
import { getMetaContent } from 'common/utils/headers'
import { toggleSaveProperty } from 'common/utils/property'
import analytics from '../services/analytics'
import type { AiAssistantFlagData } from '../types'
import auctionSmartAppImg from '../images/auction_smart_banner_logo.svg'
import { onAuthenticationComplete } from '../services/user'
import { setApplicationNotificationsContext } from '../utils/applicationNotificationsBridge'
import { appVariants } from '../reducers/app'
import Property from './property'
import CancelledProperty from './cancelledProperty'
import SmsOptInModal from './smsOptInModal'
import RedesignedProperty from './propertyRedesign'

const PDPVariant = {
  [appVariants.DEFAULT]: Property,
  [appVariants.CANCELLED]: CancelledProperty,
  [appVariants.REDESIGN]: RedesignedProperty,
}

interface AuctionAppProps {
  user: blueprint.primitives.AuctionUser
  initialListing: Listing & { active_listings?: Listing[] }
  featureFlags: FeatureFlags
  onAuthenticationComplete: any
  variant: string
  isSaved: boolean
  startTour: () => void
  ssrHints?: { announcementDismissedFingerprint?: string }
}

const AuctionApp = ({
  user,
  initialListing,
  featureFlags,
  onAuthenticationComplete,
  variant,
  startTour,
  ssrHints,
}: AuctionAppProps) => {
  const { embeddedContext } = useApplicationContext()
  const initialValues = useMemo(
    () => ({
      // Seed with the property's displayable active listings (seeded at SSR on
      // the listing) so the PDP renders its dual/single state on first paint
      // instead of flipping after the client-side fetch. Falls back to the
      // single listing if the seed is absent.
      initialData: initialListing.active_listings?.length
        ? initialListing.active_listings
        : [initialListing],
      featureFlags,
      isLoggedIn: user.logged_in,
    }),
    [initialListing, featureFlags]
  )

  const initialSavedPropertiesValues = useMemo(() => {
    const savedProperties = initialListing.primary_property.is_currently_saved
      ? [initialListing.primary_property]
      : []

    const similarProperties = initialListing.recommended_listings || []
    similarProperties.forEach((listing: any) => {
      if (listing.primary_property?.is_currently_saved) {
        savedProperties.push(listing.primary_property)
      }
    })

    return {
      initialData: savedProperties,
    }
  }, [initialListing, featureFlags])
  const { data, actions } = blueprint.listing.hooks.usePage(initialValues)
  const { data: savedPropertiesData, actions: savedPropertiesActions } =
    blueprint.user.hooks.useSavedProperties(initialSavedPropertiesValues)

  // Toggling between a property's active listings swaps the current listing
  // in place. We (1) cross-fade the swap via the View Transitions API and
  // (2) preserve scroll position across the bid-box re-resolve reflow.
  // Handled here in AuctionApp because it stays mounted across the toggle.
  const scrollYRef = useRef<number | null>(null)
  const listingActions = useMemo(
    () => ({
      ...actions,
      setCurrentListingId: (id: string) => {
        scrollYRef.current =
          typeof window !== 'undefined' ? window.scrollY : null
        const runUpdate = () => actions.setCurrentListingId(id)
        // @ts-ignore startViewTransition not yet in TS lib DOM here
        if (typeof document !== 'undefined' && document.startViewTransition) {
          // @ts-ignore
          document.startViewTransition(() => flushSync(runUpdate))
        } else {
          runUpdate()
        }
      },
    }),
    [actions]
  )

  const currentListingId = data.currentListing?.listing_id

  useEffect(() => {
    const target = scrollYRef.current
    scrollYRef.current = null
    if (
      target == null ||
      typeof window === 'undefined' ||
      typeof ResizeObserver === 'undefined'
    ) {
      return undefined
    }
    let cancelled = false
    window.scrollTo(0, target)
    // Re-apply the saved position while the page reflows (bid box settling),
    // until the user scrolls or a short window elapses.
    const observer = new ResizeObserver(() => {
      if (!cancelled) window.scrollTo(0, target)
    })
    observer.observe(document.documentElement)
    const stop = () => {
      cancelled = true
      observer.disconnect()
      clearTimeout(timer)
      window.removeEventListener('wheel', stop)
      window.removeEventListener('touchmove', stop)
      window.removeEventListener('keydown', stop)
    }
    const timer = setTimeout(stop, 1500)
    window.addEventListener('wheel', stop, { passive: true })
    window.addEventListener('touchmove', stop, { passive: true })
    window.addEventListener('keydown', stop)
    return stop
  }, [currentListingId])

  const propertyActions = {
    toggleSaveProperty: toggleSaveProperty(
      savedPropertiesActions.updateSaveProperty,
      user
    ),
  }

  // Need to review this page type because without it we're gunna have camelCase and snake_case all over the place with
  // no discernable reason. Data is mostly coupled with sdk types, so should be snake_case.
  const listing = data.currentListing

  const property = {
    ...listing?.primary_property,
    // This attached information about the status of the save of this property
    ...savedPropertiesData.savedProperties.mapped.get(
      listing?.primary_property?.property_id
    ),
  }

  // used to hand off element where we want to render page banner
  // TODO: Use __methods__ here
  const isPrivateSeller = blueprint.listing.utils.isPrivateSeller(listing)
  const pageBannerRef = useRef(null)
  const addressString = Address.format(listing)
  const websiteName = isPrivateSeller ? 'SmartSale' : 'Auction.com'
  const headerTitle = addressString
    ? `${addressString} | ${websiteName}`
    : websiteName

  const metaContent = getMetaContent(listing)
  const isNewNavigationEnabled = featureFlags['fe.resi.enable-new-navigation']
  const handleSearchSubmit = (suggestion: { link: string }) => {
    analytics.trackSearchSubmit(user)
    window.location.assign(suggestion.link)
  }
  const headerProps = {
    featureFlags,
    showLiveChat: true,
    showSearch: true,
    onSearchSubmit: handleSearchSubmit,
    ...(isNewNavigationEnabled
      ? {
          enableNewMobileHeader: true,
          customMobileContent: (
            <AdcSearch
              elmId='pdp_mobile_search'
              user={user}
              categorizeResults
              variant='new-mobile-header'
            />
          ),
          newMobileHeaderLogoVariant: 'icon' as const,
        }
      : {}),
  }
  const footerProps = {
    useDeprecatedPageWidth: true,
  }

  const ADC_LOGO = config.get('AUCTIONCOM_LOGO_URL', '')
  const headerDataStructureSchemas = [
    standardSchemas.corporateSchema(ADC_LOGO),
    standardSchemas.searchSiteSchema(),
    standardSchemas.breadCrumbSchema(listing),
    standardSchemas.eventSchema(
      listing,
      config.get('LISTING_DEFAULT_PHOTO_URL', '')
    ),
    standardSchemas.productSchema(
      listing,
      config.get('LISTING_DEFAULT_PHOTO_URL', '')
    ),
    standardSchemas.organizationSchema(ADC_LOGO),
    standardSchemas.webPageSchema(
      'https://www.auction.com/details',
      headerTitle,
      metaContent,
      standardSchemas.breadCrumbSchema(listing),
      [
        `${listing?.primary_property?.summary?.address.country_secondary_subdivision}`,
        `${listing?.primary_property?.summary?.address.country_secondary_subdivision} County`,
        `${listing?.primary_property.summary?.address.country_primary_subdivision}`,
        `${listing?.primary_property.summary?.address.street_name}`,
        `${listing?.primary_property.summary?.address.municipality}`,
        `${listing?.primary_property.summary?.street_address}`,
      ]
    ),
    ...(property.summary?.structure_type_group ===
    enums.PropertyStructureTypeGroup.SingleFamily
      ? [standardSchemas.singleFamilyHomeSchema(listing)]
      : []),
  ]

  const helmetConfig = {
    title: `${headerTitle}`,
    description: metaContent,
    children: (
      <script type='application/ld+json'>
        {JSON.stringify(headerDataStructureSchemas)}
      </script>
    ),
  }

  const capabilities = useMemo(() => {
    return {
      notifications: {
        pageBannerRef,
      },
      confirmation: {
        enabled: true,
      },
      pageTracking: {
        enabled: true,
        options: {
          domainEvents: true,
        },
      },
      authentication: {
        enabled: true,
        options: {
          onAuthenticationComplete: (props: {
            user: blueprint.primitives.AuctionUser
          }) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
            onAuthenticationComplete({
              ...props,
              refreshPageData: actions.refresh,
              listing,
            }),
        },
      },
      onboarding: {
        enabled: true,
        hideIfEmbedded: true,
        startTour,
      },
    }
  }, [onAuthenticationComplete, pageBannerRef])

  const SelectedPDP: (props: any) => React.JSX.Element = PDPVariant[variant]

  /**
   * We allow the default footer to come through as it knows how to behave when it's in webview mode...
   */
  const overridableComponents =
    embeddedContext === 'webview'
      ? {
          header: <NullComponent />,
        }
      : {}

  const aiFlagData = featureFlags['fe.resi.ai-assistant'] as
    | AiAssistantFlagData
    | undefined

  const showChatButton = !aiFlagData?.enabled

  return (
    <AuctionApplication
      user={user}
      sspConfig={
        listing.listing_configuration?.asset_type ===
        graph.enums.AssetType.BankOwned
          ? _.omit(config.get(), [
              'appstore.adc.GOOGLE_PLAY_ID',
              'appstore.adc.APPLE_ITUNES_ID',
            ])
          : config.get()
      }
      headerProps={headerProps}
      footerProps={footerProps}
      helmetConfig={helmetConfig}
      featureFlags={featureFlags}
      capabilities={capabilities}
      overridableComponents={overridableComponents}
      domainEventSource={listing.urn}
      ssrHints={ssrHints}
    >
      <Helmet
        link={[
          { rel: 'android-touch-icon', href: auctionSmartAppImg },
          { rel: 'apple-touch-icon', href: auctionSmartAppImg },
        ]}
      />
      <ApplicationNotificationsConsumer>
        {
          // @ts-ignore no types
          context => {
            setApplicationNotificationsContext(context)
            return null
          }
        }
      </ApplicationNotificationsConsumer>
      <main className='enhanced-drawer-pushable'>
        <MarketingAnalysisProvider>
          <SelectedPDP
            pageBannerRef={pageBannerRef}
            initialListing={listing}
            activeListings={data.activeListings.raw}
            listingActions={listingActions}
            property={property}
            propertyActions={propertyActions}
            savedPropertiesData={savedPropertiesData}
          />
          {showChatButton && <ChatButton />}
          <SmsOptInModal />
        </MarketingAnalysisProvider>
      </main>
    </AuctionApplication>
  )
}

// TODO: type these and the stores, it can be done
const mapStateToProps = (store: any) => ({
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  initialListing: store.listing,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  user: store.user,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  featureFlags: store.featureFlags,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  variant: store.app.variant,
})

const mapDispatchToProps = (dispatch: any) => ({
  // AuthModal required capabilites
  onAuthenticationComplete: (props: {
    user: blueprint.primitives.AuctionUser
    refreshPageData: ListingPageActionsType['refresh']
    listing: ListingPageType['currentListing']
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  }) => dispatch(onAuthenticationComplete(props)),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  startTour: () => dispatch(TutorialTooltipActions.incrementTutorial()),
})

export default withApplicationContext(
  connect(mapStateToProps, mapDispatchToProps)(AuctionApp)
)
